import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Header from '../../components/Header';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { useToast } from '../../components/Toast';
import useUrlState from '../../hooks/useUrlState';
import extractApiError from '../../utils/apiError';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { logError } from '../../utils/logError';
import { parseTutarTR, formatTutarTR } from '../../utils/para';
import SearchableSelect from '../../components/SearchableSelect';

// PR-C (PC6a) — Tahsilatlar: liste + yeni tahsilat (müşteri açık alacaklarına eşleştirme).
// /api/tahsilatlar (GET liste, POST kaydet, POST {id}/iptal). Müşteri seçilince
// /api/alacaklar/acik?cari_id= ile açık faturalar gelir → satır başına eşleşen tutar.
// Toplam tahsilat > eşleşen toplam ise fazla = avans (backend TahsilatAvansi yaratır).
// Anayasa M.5: MANAGE_TAHSILAT ile mutation; backend zaten enforce eder.

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const YONTEM_OPTIONS = [
  { value: 'HAVALE', label: 'Havale / EFT' },
  { value: 'KART', label: 'Kredi Kartı' },
];

const DURUM_BADGE = {
  TAMAMLANDI: { bg: '#d4edda', fg: '#155724', label: 'Tamamlandı' },
  IPTAL: { bg: '#f8d7da', fg: '#721c24', label: 'İptal' },
};

const fmtTarih = (val) => {
  if (!val) return '-';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val;
  return d.toLocaleDateString('tr-TR');
};

const bugunISO = () => new Date().toISOString().split('T')[0];

const DurumBadge = ({ durum }) => {
  const b = DURUM_BADGE[durum] || { bg: colors.backgroundAlt, fg: colors.textSecondary, label: durum || '-' };
  return (
    <span style={{
      padding: '4px 10px', borderRadius: radius.lg, fontSize: '12px', fontWeight: 'bold',
      background: b.bg, color: b.fg, whiteSpace: 'nowrap',
    }}>
      {b.label}
    </span>
  );
};

const inputStyle = {
  width: '100%', padding: '10px', boxSizing: 'border-box',
  borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '14px',
};
const labelStyle = { display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px' };

const TahsilatlarPage = () => {
  const { user } = useAuth();
  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_TAHSILAT, user.effectivePermissions);
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [cariMap, setCariMap] = useState({});       // cari_id → unvan
  const [cariler, setCariler] = useState([]);        // SearchableSelect seçenekleri
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // Yeni tahsilat modal state'i
  const [showModal, setShowModal] = useState(false);
  const [cariId, setCariId] = useState('');
  const [acikAlacaklar, setAcikAlacaklar] = useState([]);
  const [acikLoading, setAcikLoading] = useState(false);
  const [eslesen, setEslesen] = useState({});        // acik_alacak_id → "tutar" (string)
  const [tutar, setTutar] = useState('');
  const [yontem, setYontem] = useState('HAVALE');
  const [tarih, setTarih] = useState(bugunISO());
  const [referans, setReferans] = useState('');
  const [saving, setSaving] = useState(false);

  const [filtreCari, setFiltreCari] = useUrlState('tah_cari', '');
  const [baslangic, setBaslangic] = useUrlState('tah_baslangic', '');
  const [bitis, setBitis] = useUrlState('tah_bitis', '');

  const token = localStorage.getItem('access_token');

  // Müşteri listesi (eşleme + dropdown) — bir kez yüklenir.
  useEffect(() => {
    fetch(`${API}/api/cari?tip=MUSTERI&include_inactive=true`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        const map = {};
        list.forEach((c) => { map[c.id] = c.unvan; });
        setCariMap(map);
        setCariler(list);
      })
      .catch(() => {});
  }, [token]);

  const fetchRows = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filtreCari) params.set('cari_id', filtreCari);
      if (baslangic) params.set('baslangic', baslangic);
      if (bitis) params.set('bitis', bitis);
      const res = await fetch(`${API}/api/tahsilatlar?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(extractApiError(data.detail));
        return;
      }
      const aktifTahsilatlar = Array.isArray(data)
        ? data.filter((t) => String(t.durum || '').toUpperCase() !== 'IPTAL')
        : [];

      setRows(aktifTahsilatlar);
    } catch (err) {
      logError('Tahsilat yükleme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  }, [token, filtreCari, baslangic, bitis, toast]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const cariAd = (id) => cariMap[id] || `#${id}`;

  // --- Yeni tahsilat akışı ---
  const openNew = () => {
    setCariId('');
    setAcikAlacaklar([]);
    setEslesen({});
    setTutar('');
    setYontem('HAVALE');
    setTarih(bugunISO());
    setReferans('');
    setShowModal(true);
  };

  // Müşteri seçilince açık alacaklarını çek; eşleşen tutarları kalan ile öner.
  const handleCariSecim = async (id) => {
    setCariId(id);
    setAcikAlacaklar([]);
    setEslesen({});
    if (!id) return;
    setAcikLoading(true);
    try {
      const res = await fetch(`${API}/api/alacaklar/acik?cari_id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) {
        toast.error(extractApiError(data.detail));
        return;
      }
      const list = Array.isArray(data) ? data : [];
      setAcikAlacaklar(list);
      // Eşleşmeleri başlangıçta boş bırak. Tahsilat tutarı girildiğinde
      // en eski açık faturadan başlayarak otomatik dağıtılacak.
      const def = {};
      list.forEach((a) => { def[a.id] = ''; });
      setEslesen(def);
      setTutar('');
    } catch (err) {
      logError('Açık alacak yükleme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setAcikLoading(false);
    }
  };

  const setEslesenTutar = (alacakId, val) => {
    setEslesen((prev) => ({ ...prev, [alacakId]: val }));
  };

  // Tahsilatı en eski vadeli faturadan başlayarak sırayla dağıt.
  // Bir fatura tamamen kapanmadan sonraki faturaya geçilmez.
  const tahsilatiDagit = (tutarDegeri, alacaklar) => {
    let kalanTahsilat = parseTutarTR(tutarDegeri) || 0;
    const next = {};

    const sirali = [...alacaklar].sort((a, b) => {
      const tarihA = new Date(a.vade_tarihi || a.tarih || 0);
      const tarihB = new Date(b.vade_tarihi || b.tarih || 0);
      return tarihA - tarihB;
    });

    sirali.forEach((a) => {
      const kalanFatura = Number(a.kalan_tutar) || 0;
      if (kalanTahsilat <= 0) {
        next[a.id] = '';
        return;
      }
      const odenecek = Math.min(kalanFatura, kalanTahsilat);
      next[a.id] = odenecek > 0 ? String(+odenecek.toFixed(2)) : '';
      kalanTahsilat = +(kalanTahsilat - odenecek).toFixed(2);
    });

    setEslesen(next);
  };

  // Eşleşen toplam (parse edilmiş, geçersiz=0).
  const eslesenToplam = useMemo(
    () => acikAlacaklar.reduce((s, a) => s + (parseTutarTR(eslesen[a.id]) || 0), 0),
    [acikAlacaklar, eslesen]
  );
  const tutarNum = parseTutarTR(tutar) || 0;
  // Fazla ödeme → avans olarak işlenecek.
  const avans = Math.max(0, +(tutarNum - eslesenToplam).toFixed(2));

  // Herhangi bir satırda eşleşen > kalan mı?
  const eslesenAsim = useMemo(
    () => acikAlacaklar.some((a) => (parseTutarTR(eslesen[a.id]) || 0) > (Number(a.kalan_tutar) || 0) + 0.005),
    [acikAlacaklar, eslesen]
  );
  // Eşleşen toplam, tahsilat tutarını aşamaz (fark avans olur, negatif değil).
  const toplamAsim = eslesenToplam > tutarNum + 0.005;

  const kaydetDisabled = saving || !cariId || tutarNum <= 0 || eslesenAsim || toplamAsim;

  const kaydetIpucu = useMemo(() => {
    if (!cariId) return 'Müşteri seçin';
    if (tutarNum <= 0) return 'Tahsilat tutarı girin';
    if (eslesenAsim) return 'Bir satırda eşleşen tutar kalanı aşıyor';
    if (toplamAsim) return 'Eşleşen toplam tahsilat tutarını aşamaz';
    return '';
  }, [cariId, tutarNum, eslesenAsim, toplamAsim]);

  const handleKaydet = async () => {
    if (kaydetDisabled) return;
    setSaving(true);
    try {
      const eslestirmeler = acikAlacaklar
        .map((a) => ({ acik_alacak_id: a.id, eslesen_tutar: parseTutarTR(eslesen[a.id]) || 0 }))
        .filter((e) => e.eslesen_tutar > 0);
      const body = {
        cari_id: parseInt(cariId, 10),
        tutar: tutarNum,
        tarih,
        yontem,
        referans_no: referans.trim() || undefined,
        eslestirmeler,
      };
      const res = await fetch(`${API}/api/tahsilatlar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(avans > 0 ? `Tahsilat kaydedildi (₺${formatTutarTR(avans)} avans)` : 'Tahsilat kaydedildi');
        setShowModal(false);
        fetchRows();
      } else {
        toast.error(extractApiError(data.detail));
      }
    } catch (err) {
      logError('Tahsilat kaydetme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setSaving(false);
    }
  };

  const handleIptal = async (t) => {
    if (!window.confirm(
      `"${t.tahsilat_no || `#${t.id}`}" tahsilatını iptal etmek istediğinize emin misiniz?\n\nEşleştirilen alacaklar yeniden açık duruma döner.`,
    )) return;
    setBusyId(t.id);
    try {
      const res = await fetch(`${API}/api/tahsilatlar/${t.id}/iptal`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`"${t.tahsilat_no || `#${t.id}`}" iptal edildi`);
        fetchRows();
      } else {
        toast.error(extractApiError(data.detail));
      }
    } catch (err) {
      logError('Tahsilat iptal hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setBusyId(null);
    }
  };

  const cariOptions = useMemo(
    () => cariler.map((c) => ({
      value: String(c.id),
      label: c.unvan || `#${c.id}`,
      searchText: `${c.unvan || ''} ${c.kod || ''} ${c.vergi_no || ''}`,
    })),
    [cariler]
  );
  const filtreCariOptions = useMemo(
    () => [{ value: '', label: 'Tüm Müşteriler' }, ...cariOptions],
    [cariOptions]
  );

  const yontemLabel = (kod) => YONTEM_OPTIONS.find((y) => y.value === kod)?.label || kod || '-';

  if (loading && rows.length === 0) {
    return (
      <div>
        <Header title="Tahsilatlar" />
        <div style={{ padding: '40px', textAlign: 'center', color: colors.textSecondary }}>Yükleniyor…</div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Tahsilatlar" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Üst bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: colors.textPrimary }}>💵 Tahsilatlar</h2>
          {canManage && (
            <button
              onClick={openNew}
              style={{ padding: '10px 20px', background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', whiteSpace: 'nowrap' }}
            >
              + Yeni Tahsilat
            </button>
          )}
        </div>

        {/* Filtreler */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '220px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: colors.textSecondary, marginBottom: '4px', fontWeight: 'bold' }}>Müşteri</label>
            <SearchableSelect options={filtreCariOptions} value={filtreCari} onChange={setFiltreCari} placeholder="Tüm Müşteriler" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: colors.textSecondary, marginBottom: '4px', fontWeight: 'bold' }}>Başlangıç</label>
            <input type="date" value={baslangic} onChange={(e) => setBaslangic(e.target.value)} style={{ padding: '9px 10px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '14px' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: colors.textSecondary, marginBottom: '4px', fontWeight: 'bold' }}>Bitiş</label>
            <input type="date" value={bitis} onChange={(e) => setBitis(e.target.value)} style={{ padding: '9px 10px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '14px' }} />
          </div>
        </div>

        {/* Tablo */}
        <div style={{ background: colors.surface, borderRadius: radius.md, boxShadow: shadows.sm, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>Tahsilat No</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>Tarih</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>Müşteri</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right' }}>Tutar</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center' }}>Yöntem</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center' }}>Durum</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', width: '120px' }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '13px', color: '#1565c0' }}>{t.tahsilat_no || `#${t.id}`}</td>
                    <td style={{ padding: '10px 12px' }}>{fmtTarih(t.tarih)}</td>
                    <td style={{ padding: '10px 12px' }}><strong>{cariAd(t.cari_id)}</strong></td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {t.tutar != null ? `${formatTutarTR(t.tutar)} TL` : '-'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>{yontemLabel(t.yontem)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}><DurumBadge durum={t.durum} /></td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      {canManage && t.durum === 'TAMAMLANDI' && (
                        <button
                          onClick={() => handleIptal(t)}
                          disabled={busyId === t.id}
                          style={{ padding: '6px 12px', borderRadius: radius.sm, border: 'none', background: '#d32f2f', color: 'white', cursor: busyId === t.id ? 'wait' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                        >
                          İptal
                        </button>
                      )}
                    </td>
                  </tr>
                ))}

                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>
                      {filtreCari || baslangic || bitis ? 'Kritere uygun tahsilat bulunamadı.' : 'Henüz tahsilat kaydı yok.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Yeni Tahsilat modal */}
        {showModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', boxSizing: 'border-box',
          }}>
            <div style={{ background: colors.surface, padding: '30px', borderRadius: radius.lg, width: '640px', maxWidth: '94vw', boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>Yeni Tahsilat</h3>

              <label style={labelStyle}>Müşteri *</label>
              <div style={{ marginBottom: '14px' }}>
                <SearchableSelect options={cariOptions} value={cariId} onChange={handleCariSecim} placeholder="Müşteri seçin…" />
              </div>

              {/* Açık alacak eşleştirme tablosu */}
              {cariId && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Açık Alacaklar (eşleştirme)</label>
                  {acikLoading ? (
                    <div style={{ padding: '12px', color: colors.textSecondary, fontSize: '13px' }}>Açık alacaklar yükleniyor…</div>
                  ) : acikAlacaklar.length === 0 ? (
                    <div style={{ padding: '12px', color: colors.textTertiary, fontSize: '13px', background: colors.backgroundAlt, borderRadius: radius.sm }}>
                      Bu müşterinin açık alacağı yok. Girilen tutarın tamamı avans olarak kaydedilir.
                    </div>
                  ) : (
                    <div style={{ border: `1px solid ${colors.border}`, borderRadius: radius.sm, overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ background: colors.backgroundAlt }}>
                            <th style={{ padding: '8px 10px', textAlign: 'left' }}>Fatura No</th>
                            <th style={{ padding: '8px 10px', textAlign: 'left' }}>Vade</th>
                            <th style={{ padding: '8px 10px', textAlign: 'right' }}>Kalan</th>
                            <th style={{ padding: '8px 10px', textAlign: 'right', width: '140px' }}>Eşleşen Tutar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {acikAlacaklar.map((a) => {
                            const kalan = Number(a.kalan_tutar) || 0;
                            const es = parseTutarTR(eslesen[a.id]) || 0;
                            const asim = es > kalan + 0.005;
                            return (
                              <tr key={a.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                                <td style={{ padding: '8px 10px', fontFamily: 'monospace', color: '#1565c0' }}>{a.fatura_no || `#${a.id}`}</td>
                                <td style={{ padding: '8px 10px' }}>{fmtTarih(a.vade_tarihi)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTutarTR(kalan)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                                  <input
                                    type="text"
                                    value={eslesen[a.id] ?? ''}
                                    onChange={(e) => setEslesenTutar(a.id, e.target.value)}
                                    style={{
                                      width: '120px', padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace',
                                      border: `${asim ? 2 : 1}px solid ${asim ? colors.danger : colors.border}`,
                                      borderRadius: radius.sm, boxSizing: 'border-box', fontSize: '13px',
                                    }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Tahsilat alanları */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Tahsilat Tutarı (TL) *</label>
                  <input
                    type="text"
                    value={tutar}
                    onChange={(e) => {
                      const val = e.target.value;
                      setTutar(val);
                      tahsilatiDagit(val, acikAlacaklar);
                    }}
                    placeholder="0,00"
                    style={{ ...inputStyle, textAlign: 'right', fontFamily: 'monospace' }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Yöntem</label>
                  <select value={yontem} onChange={(e) => setYontem(e.target.value)} style={{ ...inputStyle }}>
                    {YONTEM_OPTIONS.map((y) => (
                      <option key={y.value} value={y.value}>{y.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ height: '12px' }} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Tarih</label>
                  <input type="date" value={tarih} onChange={(e) => setTarih(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Banka / Referans No</label>
                  <input type="text" value={referans} onChange={(e) => setReferans(e.target.value)} placeholder="Opsiyonel" style={inputStyle} />
                </div>
              </div>

              {/* Eşleşen vs tutar özeti */}
              <div style={{
                marginTop: '16px', padding: '12px 14px', borderRadius: radius.sm,
                background: colors.backgroundAlt, fontSize: '13px',
                display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
              }}>
                <span style={{ color: colors.textSecondary }}>
                  Eşleşen toplam: <strong style={{ fontFamily: 'monospace', color: toplamAsim ? colors.danger : colors.textPrimary }}>{formatTutarTR(eslesenToplam)} TL</strong>
                </span>
                <span style={{ color: colors.textSecondary }}>
                  Tahsilat: <strong style={{ fontFamily: 'monospace' }}>{formatTutarTR(tutarNum)} TL</strong>
                </span>
                {avans > 0 && (
                  <span style={{ color: colors.info, fontWeight: 'bold' }}>
                    Avans: {formatTutarTR(avans)} TL
                  </span>
                )}
              </div>

              {kaydetIpucu && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: colors.danger }}>{kaydetIpucu}</div>
              )}

              <div style={{ height: '20px' }} />

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleKaydet}
                  disabled={kaydetDisabled}
                  title={kaydetIpucu || undefined}
                  style={{
                    flex: 1, padding: '12px', border: 'none', borderRadius: radius.sm,
                    background: kaydetDisabled ? '#90caf9' : '#1565c0', color: 'white',
                    cursor: kaydetDisabled ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '14px',
                  }}
                >
                  {saving ? 'Kaydediliyor…' : 'Kaydet'}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    flex: 1, padding: '12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
                    background: colors.background, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
                  }}
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default TahsilatlarPage;
